/**
 * Post-authentication redirect helpers (CB25SES-553).
 *
 * PRODUCT_HOME_PATH is the canonical product homepage route and the single
 * source of truth reused by app routing and future post-auth redirects (e.g.
 * the accept-invitation page, keycloak redirect).
 *
 * `resolvePostAuthTarget` / `isSafeInternalPath` provide open-redirect
 * protection for any flow that resolves a stored/return URL: a safe internal
 * path is preserved, while external or malformed values fall back to the
 * homepage.
 */
export const PRODUCT_HOME_PATH = '/home';

/**
 * The screen PRODUCT_HOME_PATH resolves to.
 *
 * There is no separate homepage: the projects list is the product's landing
 * screen, so /home only forwards here. Declared next to PRODUCT_HOME_PATH so
 * the route, the sidebar and the brand agree on one destination — in-app
 * navigation links straight here rather than bouncing through /home, and
 * /home stays the stable target for anything outside the app (Keycloak
 * return_to, invitation links) that cannot know this.
 */
export const PRODUCT_LANDING_PATH = '/projects';

/**
 * Fixed, non-resolvable sentinel origin used only as a parse base for the URL
 * constructor. `.invalid` is a reserved TLD (RFC 2606) that can never be a
 * real host. A root-relative path inherits this origin (safe);
 * a path carrying its own origin overrides it (external -> rejected).
 */
const SENTINEL_ORIGIN = 'http://internal.invalid';

/**
 * True only for safe, same-origin, root-relative paths.
 *
 * Use this when you need a yes/no verdict on an untrusted path without
 * substituting a fallback — for example to decide whether to show an error
 * instead of silently redirecting somewhere else. If you just need a usable
 * destination, call `resolvePostAuthTarget` instead.
 *
 * @example
 * isSafeInternalPath('/projects/123');          // true
 * isSafeInternalPath('/projects/123?tab=team'); // true
 * isSafeInternalPath('https://evil.com');       // false (carries its own origin)
 * isSafeInternalPath('//evil.com');             // false (protocol-relative)
 * isSafeInternalPath('/\\evil.com');            // false (backslash trick)
 * isSafeInternalPath('projects/123');           // false (not root-relative)
 * isSafeInternalPath('');                       // false
 */
export function isSafeInternalPath(path: string | null | undefined): boolean {
  if (!path || typeof path !== 'string') return false;

  // Must be root-relative.
  if (!path.startsWith('/')) return false;

  // Reject protocol-relative ("//evil.com") and backslash tricks ("/\evil.com").
  if (path.startsWith('//') || path.startsWith('/\\')) return false;

  // Reject anything that resolves to a different origin.
  try {
    const url = new URL(path, SENTINEL_ORIGIN);
    if (url.origin !== SENTINEL_ORIGIN) return false;
  } catch {
    return false;
  }

  return true;
}

/**
 * Resolves the destination after a successful auth flow.
 * - empty / "/" / malformed / external -> product homepage
 * - safe internal deep link            -> that deep link
 *
 * Always returns a non-empty, internal path, so callers never have to
 * null-check the result or decide on a fallback themselves.
 *
 * @example
 * resolvePostAuthTarget('/projects/123');   // '/projects/123'  (deep link kept)
 * resolvePostAuthTarget('/');               // '/home'
 * resolvePostAuthTarget('');                // '/home'
 * resolvePostAuthTarget(null);              // '/home'
 * resolvePostAuthTarget('https://evil.com') // '/home'          (external rejected)
 *
 * @example
 * // Typical use: capture where the user is now, so they come back here after
 * // authenticating. The backend login endpoint reads the `return_to` query
 * // parameter (snake_case) and sanitises it again server-side.
 * const current =
 *   window.location.pathname + window.location.search + window.location.hash;
 * const returnTo = resolvePostAuthTarget(current);
 * window.location.assign(
 *   `${backendBaseUrl}/auth/login?return_to=${encodeURIComponent(returnTo)}`,
 * );
 *
 * @example
 * // Reading a return URL back out of a query parameter before navigating.
 * const raw = new URLSearchParams(window.location.search).get('return_to');
 * navigate(resolvePostAuthTarget(raw), { replace: true });
 */
export function resolvePostAuthTarget(
  storedUrl: string | null | undefined,
): string {
  if (!storedUrl || storedUrl === '/') return PRODUCT_HOME_PATH;
  if (!isSafeInternalPath(storedUrl)) return PRODUCT_HOME_PATH;
  return storedUrl;
}
